if (!customElements.get('tp-reviews-slider')) {
  customElements.define(
    'tp-reviews-slider',
    class TpReviewsSlider extends HTMLElement {
      connectedCallback() {
        this.scroller = this.querySelector('.tp-reviews__scroller');
        this.track = this.querySelector('.tp-reviews__track');
        const root = this.closest('.tp-reviews');
        this.fill = root ? root.querySelector('.js-tp-reviews-thumb') : null;
        this.progress = root ? root.querySelector('.tp-reviews__progress') : null;
        if (!this.scroller || !this.track) return;

        this.offset = 0;
        this.dragging = false;
        this.startX = 0;
        this.startOffset = 0;
        this.lastX = 0;
        this.lastTime = 0;
        this.velocity = 0;
        this.wheelLock = false;
        this.fillRaf = 0;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onProgressClick = this.onProgressClick.bind(this);
        this.onWheel = this.onWheel.bind(this);
        this.onTransitionEnd = this.onTransitionEnd.bind(this);

        this.scroller.addEventListener('pointerdown', this.onPointerDown);
        this.scroller.addEventListener('wheel', this.onWheel, { passive: false });
        this.track.addEventListener('transitionend', this.onTransitionEnd);
        window.addEventListener('resize', this.onResize);
        if (this.progress) this.progress.addEventListener('click', this.onProgressClick);

        this.setOffset(0, false);
      }

      disconnectedCallback() {
        if (!this.scroller) return;
        this.scroller.removeEventListener('pointerdown', this.onPointerDown);
        this.scroller.removeEventListener('wheel', this.onWheel);
        if (this.track) this.track.removeEventListener('transitionend', this.onTransitionEnd);
        window.removeEventListener('resize', this.onResize);
        if (this.progress) this.progress.removeEventListener('click', this.onProgressClick);
        this.removeDragListeners();
        this.stopFillWatch();
      }

      slideStep() {
        const card = this.track.querySelector('.tp-reviews__card');
        if (!card) return this.scroller.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      maxOffset() {
        return Math.max(0, this.track.scrollWidth - this.scroller.clientWidth);
      }

      clamp(value) {
        return Math.min(this.maxOffset(), Math.max(0, value));
      }

      setOffset(value, animate) {
        this.offset = this.clamp(value);
        this.track.style.transition = animate ? 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
        this.track.style.transform = 'translate3d(' + -this.offset + 'px, 0, 0)';
        if (animate) {
          this.watchFill();
        } else {
          this.updateFill(this.offset);
        }
      }

      currentIndex() {
        const step = this.slideStep();
        if (step <= 0) return 0;
        return Math.round(this.offset / step);
      }

      maxIndex() {
        const step = this.slideStep();
        if (step <= 0) return 0;
        return Math.round(this.maxOffset() / step);
      }

      goTo(index) {
        const i = Math.min(this.maxIndex(), Math.max(0, index));
        this.setOffset(i * this.slideStep(), true);
      }

      displayedOffset() {
        const transform = getComputedStyle(this.track).transform;
        if (!transform || transform === 'none') return this.offset;
        const values = transform.replace(/^matrix3d\(|^matrix\(|\)$/g, '').split(',');
        const x = transform.indexOf('matrix3d') === 0 ? values[12] : values[4];
        return Math.abs(parseFloat(x) || 0);
      }

      updateFill(offset) {
        if (!this.fill) return;
        const total = this.track.scrollWidth;
        if (total <= 0) return;
        const x = typeof offset === 'number' ? offset : this.displayedOffset();
        const seen = (x + this.scroller.clientWidth) / total;
        this.fill.style.width = Math.min(1, Math.max(0, seen)) * 100 + '%';
      }

      watchFill() {
        this.stopFillWatch();
        const tick = () => {
          this.updateFill();
          this.fillRaf = requestAnimationFrame(tick);
        };
        this.fillRaf = requestAnimationFrame(tick);
      }

      stopFillWatch() {
        if (this.fillRaf) {
          cancelAnimationFrame(this.fillRaf);
          this.fillRaf = 0;
        }
      }

      onTransitionEnd(event) {
        if (event.propertyName !== 'transform') return;
        this.stopFillWatch();
        this.updateFill(this.offset);
      }

      onWheel(event) {
        if (this.maxOffset() <= 0) return;
        event.preventDefault();
        if (this.wheelLock) return;
        const delta = Math.abs(event.deltaY) > Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
        if (Math.abs(delta) < 8) return;
        this.wheelLock = true;
        this.goTo(this.currentIndex() + (delta > 0 ? 1 : -1));
        window.setTimeout(() => {
          this.wheelLock = false;
        }, 500);
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.dragging = true;
        this.startX = event.clientX;
        this.lastX = event.clientX;
        this.lastTime = Date.now();
        this.startOffset = this.displayedOffset();
        this.offset = this.startOffset;
        this.velocity = 0;
        this.scroller.classList.add('is-dragging');
        this.track.style.transition = 'none';
        this.scroller.setPointerCapture(event.pointerId);
        this.scroller.addEventListener('pointermove', this.onPointerMove);
        this.scroller.addEventListener('pointerup', this.onPointerUp);
        this.scroller.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (!this.dragging) return;
        const now = Date.now();
        const dx = event.clientX - this.lastX;
        const dt = Math.max(now - this.lastTime, 1);
        this.velocity = dx / dt;
        this.lastX = event.clientX;
        this.lastTime = now;
        this.setOffset(this.startOffset - (event.clientX - this.startX), false);
      }

      onPointerUp() {
        if (!this.dragging) return;
        this.dragging = false;
        this.scroller.classList.remove('is-dragging');
        this.removeDragListeners();

        const moved = this.offset - this.startOffset;
        const startIndex = Math.round(this.startOffset / this.slideStep());
        const flick = Math.abs(this.velocity) > 0.35;
        if (flick || Math.abs(moved) > this.slideStep() * 0.2) {
          this.goTo(startIndex + (moved > 0 ? 1 : -1));
        } else {
          this.goTo(startIndex);
        }
      }

      removeDragListeners() {
        this.scroller.removeEventListener('pointermove', this.onPointerMove);
        this.scroller.removeEventListener('pointerup', this.onPointerUp);
        this.scroller.removeEventListener('pointercancel', this.onPointerUp);
      }

      onProgressClick(event) {
        const max = this.maxOffset();
        if (max <= 0) return;
        const bounds = this.progress.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
        this.goTo(Math.round(ratio * this.maxIndex()));
      }

      onResize() {
        this.setOffset(this.offset, false);
      }
    }
  );
}
